// react
import { useEffect, useRef, useState } from 'react';

// relative
import { darkTheme, lightTheme } from '../../../components/highcharts/themes';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/xrange';
import { DateTime } from 'luxon';

const PublishedActuatorPlanChart = ({
  actuatorPlan,
  occupancyPrediction,
  generatedOccupancyPrediction,
}) => {
  const chartRef = useRef(null);
  const theme = useTheme();
  const [now] = useState(() => Date.now());
  const [options, setOptions] = useState({
    chart: {
      type: 'xrange',
      height: 300,
    },
    title: {
      text: '',
    },
    xAxis: {
      type: 'datetime',
      labels: {
        formatter: function () {
          return Highcharts.dateFormat('%A', this.value);
        },
      },
      tickInterval: 24 * 3600 * 1000, // one day
      plotLines: [
        {
          value: now, // Current timestamp
          color: 'red', // Color of the line
          dashStyle: 'Dash', // Style of the line
          width: 2, // Width of the line
          label: {
            text: 'Now', // Label for the plotLine
            align: 'right',
            rotation: 0,
            x: 40,
            style: {
              color: 'red',
              fontWeight: 'bold',
            },
          },
        },
      ],
    },
    time: { useUTC: true },
    legend: {
      layout: 'horizontal',
      enabled: true,
      align: 'center',
      verticalAlign: 'top',
      labelFormatter: function () {
        if (this.name === 'Scheduled Activations') {
          return `<span style="color: hsl(63, 100%, 40%)">&#x25CF;</span> Scheduled Activations`;
        } else if (this.name === 'Occupancy Prediction') {
          return `<span style="color: hsl(260, 100%, 40%)">&#x25CF;</span> Occupied Prediction`;
        }
      },
    },
    yAxis: {
      title: {
        text: null, // remove the title
      },
      categories: [], // remove the category
      reversed: false,
      visible: false, // hide the y-axis
    },
    series: [
      {
        name: 'Scheduled Activations',
        pointWidth: 75,
        opacity: 0.9,
        data: [],
        legendSymbol: 'none', // Hide the symbol in the legend
        showInLegend: true,
        symbolWidth: 0, // No symbol for this series
      },
      {
        name: 'Occupancy Prediction',
        pointWidth: 75,
        opacity: 0.9,
        data: [],
        legendSymbol: 'none', // Hide the symbol in the legend
        showInLegend: true,
        symbolWidth: 0, // No symbol for this series
      },
    ],
    tooltip: {
      split: true,
      headerFormat: '',
      pointFormatter: function () {
        let duration = this.x2 - this.x;
        let hours = Math.floor(duration / 3600000);
        let minutes = Math.floor((duration % 3600000) / 60000);
        let seconds = Math.floor((duration % 60000) / 1000);
        let hoursFormatted = ('0' + hours).slice(-2);
        let minutesFormatted = ('0' + minutes).slice(-2);
        let secondsFormatted = ('0' + seconds).slice(-2);
        let tooltipHtml = `<b>${this.series.name}</b><br/>`;
        tooltipHtml += `<span>${Highcharts.dateFormat(
          '%A, %b %e, %Y %H:%M:%S',
          this.x,
        )} - ${Highcharts.dateFormat(
          '%A, %b %e, %Y %H:%M:%S',
          this.x2,
        )}</span>`;
        tooltipHtml += `<br/>&#x23F1; ${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`;
        return tooltipHtml;
      },
    },
  });

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;
    Highcharts.setOptions(themeOptions);

    if (chartRef.current && chartRef.current.chart) {
      chartRef.current.chart.update({
        chart: {
          backgroundColor: themeOptions.chart.backgroundColor,
        },
        title: {
          style: {
            color: themeOptions.title.style.color,
          },
        },
        xAxis: {
          gridLineColor: themeOptions.xAxis.gridLineColor,
          lineColor: themeOptions.xAxis.lineColor,
          labels: {
            style: {
              color: themeOptions.xAxis.labels.style.color,
            },
          },
        },
        yAxis: [
          {
            gridLineColor: themeOptions.yAxis.gridLineColor,
            labels: {
              style: {
                color: themeOptions.yAxis.labels.style.color,
              },
            },
          },
        ],
        tooltip: {
          backgroundColor: themeOptions.tooltip.backgroundColor,
          borderColor: themeOptions.tooltip.borderColor,
        },
      });
    }
  }, [theme]);

  useEffect(() => {
    if (actuatorPlan) {
      const offset = 1 * 60 * 60 * 1000;
      const formattedData = actuatorPlan.actuator_plan?.map((interval) => ({
        x:
          DateTime.fromISO(interval.interval_start, { zone: 'utc' })
            .toLocal()
            .toMillis() + offset,
        x2:
          DateTime.fromISO(interval.interval_end, { zone: 'utc' })
            .toLocal()
            .toMillis() + offset,
        y: 0,
        color: interval.setpoint === 100 ? 'hsl(63, 100%, 40%)' : 'transparent',
        borderColor: 'transparent',
      }));
      setOptions((prevOptions) => ({
        ...prevOptions,
        series: [
          { ...prevOptions.series[0], data: formattedData },
          prevOptions.series[1], // Keep the second series unchanged
        ],
      }));
    }
  }, [actuatorPlan]);

  useEffect(() => {
    if (generatedOccupancyPrediction !== null) {
      const offset = 1 * 60 * 60 * 1000;
      const formattedOccupancyData = generatedOccupancyPrediction?.map(
        (interval) => ({
          x:
            DateTime.fromISO(interval.interval_start, { zone: 'utc' })
              .toLocal()
              .toMillis() + offset,
          x2:
            DateTime.fromISO(interval.interval_end, { zone: 'utc' })
              .toLocal()
              .toMillis() + offset,
          y: 0,
          color: interval.label ? 'hsl(260, 100%, 40%)' : 'transparent',
          borderColor: 'transparent',
        }),
      );
      setOptions((prevOptions) => ({
        ...prevOptions,
        series: [
          prevOptions.series[0], // Keep the first series unchanged
          { ...prevOptions.series[1], data: formattedOccupancyData },
        ],
      }));
    } else {
      const offset = 1 * 60 * 60 * 1000;
      const formattedOccupancyData = occupancyPrediction?.occupancy_plan?.map(
        (interval) => ({
          x:
            DateTime.fromISO(interval.interval_start, { zone: 'utc' })
              .toLocal()
              .toMillis() + offset,
          x2:
            DateTime.fromISO(interval.interval_end, { zone: 'utc' })
              .toLocal()
              .toMillis() + offset,
          y: 0,
          color: interval.label ? 'hsl(260, 100%, 40%)' : 'transparent',
          borderColor: 'transparent',
        }),
      );
      setOptions((prevOptions) => ({
        ...prevOptions,
        series: [
          prevOptions.series[0], // Keep the first series unchanged
          { ...prevOptions.series[1], data: formattedOccupancyData },
        ],
      }));
    }
  }, [occupancyPrediction, generatedOccupancyPrediction]);

  return (
    <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
  );
};

export default PublishedActuatorPlanChart;
